'use client';
import React, { useState } from 'react';
import { Flame, Zap, Activity, Dumbbell, Calendar, ChevronDown, ChevronUp, Clock, RotateCcw, Save, CheckCircle2, Loader2, Info, Tag, Target, Share2, Copy, RefreshCw, ChevronRight } from 'lucide-react';
import type { TrainingProgram, WorkoutDay, Exercise, BiotensegrityTag } from '@/lib/programBuilder';
import { BIOTENSEGRITY_PRINCIPLES, SESSION_STRUCTURE } from '@/lib/programBuilder';

const PILLAR_META = {
  engine: { icon: Flame, color: '#8B0000', label: 'Engine', bg: 'rgba(139,0,0,0.1)' },
  ignition: { icon: Zap, color: '#E10600', label: 'Ignition', bg: 'rgba(225,6,0,0.08)' },
  bounce: { icon: Activity, color: '#B3B3B3', label: 'Bounce', bg: 'rgba(179,179,179,0.08)' },
  integrated: { icon: Dumbbell, color: '#FF3B30', label: 'Integrated', bg: 'rgba(255,59,48,0.08)' },
};

const INTENT_COLORS: Record<string, string> = {
  tension: '#8B0000',
  stiffness: '#B3B3B3',
  recoil: '#E10600',
  acceleration: '#FF6B35',
  transfer: '#A855F7',
};

function BiotensegrityBadge({ tag }: { tag: BiotensegrityTag }) {
  const pillarMeta = PILLAR_META[tag.pillar] ?? PILLAR_META.engine;
  const intentColor = INTENT_COLORS[tag.intent] ?? '#71717a';
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: `${pillarMeta.color}18`, color: pillarMeta.color }}>
        <Tag size={9} />
        {tag.pillar}
      </span>
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(255,255,255,0.05)', color: '#71717a' }}>
        {tag.fascialFocus}
      </span>
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: `${intentColor}15`, color: intentColor }}>
        <Target size={9} />
        {tag.intent}
      </span>
    </div>
  );
}

interface ExerciseRowProps {
  ex: Exercise;
  showBioTags: boolean;
}

function ExerciseRow({ ex, showBioTags }: ExerciseRowProps) {
  return (
    <tr className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors">
      <td className="py-2.5 px-2 min-w-[160px]">
        <p className="text-zinc-200 font-medium text-sm">{ex.name}</p>
        {showBioTags && ex.tag && <BiotensegrityBadge tag={ex.tag} />}
        {ex.intentCue && (
          <p className="text-xs text-zinc-600 mt-0.5 italic leading-tight">{ex.intentCue}</p>
        )}
      </td>
      <td className="py-2.5 px-2 font-mono tabular-nums text-zinc-300 text-center text-sm">{ex.sets}</td>
      <td className="py-2.5 px-2 font-mono tabular-nums text-zinc-300 whitespace-nowrap text-sm">{ex.reps}</td>
      <td className="py-2.5 px-2 font-mono text-zinc-400 whitespace-nowrap text-sm">
        <span className="flex items-center gap-1">
          <Clock size={10} className="text-zinc-600" />
          {ex.rest}
        </span>
      </td>
      <td className="py-2.5 px-2 font-mono text-xs whitespace-nowrap" style={{ color: '#E10600' }}>{ex.intensity}</td>
      <td className="py-2.5 px-2 text-zinc-500 text-xs max-w-[180px]">{ex.notes}</td>
    </tr>
  );
}

interface WorkoutCardProps {
  day: WorkoutDay;
  weekNumber: number;
  showBioTags: boolean;
  defaultOpen?: boolean;
}

function WorkoutCard({ day, weekNumber, showBioTags, defaultOpen = false }: WorkoutCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [showStructure, setShowStructure] = useState(false);
  const pillarMeta = PILLAR_META[day.pillar] ?? PILLAR_META.engine;
  const PillarIcon = pillarMeta.icon;

  const mainExercises = day.exercises.filter(ex => !['prep-', 'cool-'].some(p => ex.id.startsWith(p)));
  const prepExercises = day.exercises.filter(ex => ex.id.startsWith('prep-'));
  const cooldownExercises = day.exercises.filter(ex => ex.id.startsWith('cool-'));

  return (
    <div className="border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: pillarMeta.bg, border: `1px solid ${pillarMeta.color}30` }}>
            <PillarIcon size={15} style={{ color: pillarMeta.color }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-zinc-200">{day.day}</p>
            <p className="text-xs text-zinc-500">{day.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-0.5 rounded-full font-medium hidden sm:block" style={{ background: pillarMeta.bg, color: pillarMeta.color }}>
            {pillarMeta.label}
          </span>
          <span className="text-xs text-zinc-600 font-mono">{mainExercises.length} exercises</span>
          {open ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {/* Focus */}
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Info size={11} style={{ color: pillarMeta.color }} />
            <span>{day.focus}</span>
          </div>

          {/* Session Structure Toggle */}
          <button
            onClick={() => setShowStructure(!showStructure)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <ChevronRight size={11} className={`transition-transform ${showStructure ? 'rotate-90' : ''}`} />
            Session Structure
          </button>
          {showStructure && (
            <div className="pl-4 space-y-1">
              {SESSION_STRUCTURE.map((step, i) => (
                <p key={i} className="text-xs text-zinc-500">{step}</p>
              ))}
            </div>
          )}

          {/* Movement Prep */}
          {prepExercises.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Movement Prep</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      {['Exercise', 'Sets', 'Reps', 'Rest', 'Intensity', 'Notes'].map(h => (
                        <th key={h} className="text-left py-1.5 px-2 text-xs font-medium uppercase tracking-wider text-zinc-600 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {prepExercises.map(ex => <ExerciseRow key={ex.id} ex={ex} showBioTags={false} />)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Main Block */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Main Block</p>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="border-b border-zinc-800">
                    {['Exercise', 'Sets', 'Reps', 'Rest', 'Intensity', 'Notes'].map(h => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mainExercises.map(ex => <ExerciseRow key={ex.id} ex={ex} showBioTags={showBioTags} />)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cooldown */}
          {cooldownExercises.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Cooldown / Reset</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      {['Exercise', 'Sets', 'Reps', 'Rest', 'Intensity', 'Notes'].map(h => (
                        <th key={h} className="text-left py-1.5 px-2 text-xs font-medium uppercase tracking-wider text-zinc-600 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cooldownExercises.map(ex => <ExerciseRow key={ex.id} ex={ex} showBioTags={false} />)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ProgramBuilderClientProps {
  program: TrainingProgram;
  athleteName: string;
  athleteId: string | null;
  isSaving?: boolean;
  saveSuccess?: boolean;
  savedProgramId?: string | null;
  onSaveProgram?: () => Promise<void>;
  onRegenerateProgram?: () => void;
}

export default function ProgramBuilderClient({
  program,
  athleteName,
  athleteId,
  isSaving = false,
  saveSuccess = false,
  savedProgramId = null,
  onSaveProgram,
  onRegenerateProgram,
}: ProgramBuilderClientProps) {
  const [activeWeek, setActiveWeek] = useState(1);
  const [showBioTags, setShowBioTags] = useState(true);
  const [copied, setCopied] = useState(false);

  const focusMeta = PILLAR_META[program.focus] ?? PILLAR_META.engine;
  const FocusIcon = focusMeta.icon;
  const currentWeek = program.weeks.find(w => w.weekNumber === activeWeek) ?? program.weeks[0];

  const handleCopyLink = () => {
    if (savedProgramId && athleteId) {
      const url = `${window.location.origin}/athlete-dashboard?programId=${savedProgramId}`;
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div className="space-y-5">
      {/* Program Header */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: focusMeta.bg, borderColor: `${focusMeta.color}25` }}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${focusMeta.color}20` }}>
              <FocusIcon size={22} style={{ color: focusMeta.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium uppercase tracking-widest" style={{ color: focusMeta.color }}>{program.daysPerWeek} Days/Week</span>
                <span className="text-xs text-zinc-600">·</span>
                <span className="text-xs text-zinc-500">{program.duration} weeks</span>
              </div>
              <h2 className="text-lg font-semibold text-zinc-100 mb-1">{program.title}</h2>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">{program.rationale}</p>
            </div>
          </div>

          {/* Coach Controls */}
          <div className="flex flex-col gap-2 flex-shrink-0 min-w-[160px]">
            {onSaveProgram && (
              <button
                onClick={onSaveProgram}
                disabled={isSaving || saveSuccess}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: saveSuccess ? 'rgba(16,185,129,0.15)' : 'linear-gradient(135deg, #E10600, #8B0000)',
                  color: saveSuccess ? '#10B981' : '#ffffff',
                  border: saveSuccess ? '1px solid rgba(16,185,129,0.3)' : 'none',
                  boxShadow: saveSuccess ? 'none' : '0 0 16px rgba(225,6,0,0.3)',
                }}
              >
                {isSaving ? (
                  <><Loader2 size={14} className="animate-spin" /> Saving...</>
                ) : saveSuccess ? (
                  <><CheckCircle2 size={14} /> Saved</>
                ) : (
                  <><Save size={14} /> Save &amp; Assign</>
                )}
              </button>
            )}
            {onRegenerateProgram && (
              <button
                onClick={onRegenerateProgram}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#71717a' }}
              >
                <RefreshCw size={13} /> Regenerate
              </button>
            )}
            {saveSuccess && savedProgramId && (
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#71717a' }}
              >
                {copied ? <><CheckCircle2 size={13} className="text-green-400" /> Copied!</> : <><Copy size={13} /> Copy Athlete Link</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Biotensegrity Principles */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Info size={14} style={{ color: '#E10600' }} />
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Biotensegrity Training Model</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {BIOTENSEGRITY_PRINCIPLES.map((p, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-4 h-4 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">{i + 1}</span>
              <p className="text-xs text-zinc-500">{p}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Layout */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={14} style={{ color: '#E10600' }} />
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Weekly Layout</h3>
        </div>
        <div className="space-y-1.5">
          {program.weeklyLayout.map((line, i) => (
            <p key={i} className={`text-xs ${line.startsWith('Notes:') ? 'text-zinc-600 italic' : 'text-zinc-400'}`}>{line}</p>
          ))}
        </div>
      </div>

      {/* Bio Tags Toggle + Week Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-header">Weekly Schedule</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBioTags(!showBioTags)}
              className="flex items-center gap-1.5 text-xs transition-colors"
              style={{ color: showBioTags ? '#E10600' : '#71717a' }}
            >
              <Tag size={11} />
              {showBioTags ? 'Hide Tags' : 'Show Bio Tags'}
            </button>
            <span className="text-xs text-zinc-500 font-mono">{program.daysPerWeek} days/week</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {program.weeks.map(w => (
            <button
              key={`week-${w.weekNumber}`}
              onClick={() => setActiveWeek(w.weekNumber)}
              className="flex flex-col items-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150"
              style={activeWeek === w.weekNumber ? {
                border: '1px solid rgba(225,6,0,0.35)',
                background: 'rgba(225,6,0,0.1)',
                color: '#E10600',
              } : {
                border: '1px solid rgba(255,255,255,0.06)',
                background: 'rgba(255,255,255,0.03)',
                color: '#71717a',
              }}
            >
              <span className="font-mono font-bold">W{w.weekNumber}</span>
              <span className="text-zinc-600 text-xs mt-0.5 hidden sm:block">{w.theme.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Week Theme Banner */}
      {currentWeek && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(225,6,0,0.06)', border: '1px solid rgba(225,6,0,0.15)' }}>
          <Info size={14} style={{ color: '#E10600' }} className="flex-shrink-0" />
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-zinc-200">Week {currentWeek.weekNumber} — {currentWeek.theme}:</span>
            {' '}
            {program.progressionModel[Math.min(currentWeek.weekNumber - 1, program.progressionModel.length - 1)]}
          </p>
        </div>
      )}

      {/* Daily Workout Cards */}
      {currentWeek && (
        <div className="space-y-3">
          <h3 className="section-header">Week {currentWeek.weekNumber} Workouts</h3>
          {currentWeek.days.map((day, idx) => (
            <WorkoutCard
              key={`${day.id}-w${currentWeek.weekNumber}`}
              day={day}
              weekNumber={currentWeek.weekNumber}
              showBioTags={showBioTags}
              defaultOpen={idx === 0}
            />
          ))}
        </div>
      )}

      {/* Progression Model */}
      <div className="card p-5">
        <h3 className="section-header mb-4 flex items-center gap-2">
          <RotateCcw size={16} style={{ color: '#E10600' }} />
          Progression Model
        </h3>
        <div className="space-y-2">
          {program.progressionModel.map((step, i) => (
            <div key={`prog-${i}`} className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">
                {i + 1}
              </span>
              <p className="text-sm text-zinc-400">{step}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500">
            <span className="text-zinc-400 font-medium">Retest Recommendation: </span>
            {program.retestRecommendation}
          </p>
        </div>
      </div>

      {/* Coach Controls Footer */}
      {(onSaveProgram || onRegenerateProgram) && (
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Settings2Icon size={13} style={{ color: '#E10600' }} />
            Coach Controls
          </h3>
          <div className="flex flex-wrap gap-2">
            {onSaveProgram && !saveSuccess && (
              <button
                onClick={onSaveProgram}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{ background: 'linear-gradient(135deg, #E10600, #8B0000)', color: '#fff', boxShadow: '0 0 12px rgba(225,6,0,0.25)' }}
              >
                {isSaving ? <><Loader2 size={13} className="animate-spin" /> Saving...</> : <><Save size={13} /> Save &amp; Assign to Athlete</>}
              </button>
            )}
            {onRegenerateProgram && (
              <button
                onClick={onRegenerateProgram}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#a1a1aa' }}
              >
                <RefreshCw size={13} /> Edit &amp; Regenerate
              </button>
            )}
            {saveSuccess && savedProgramId && (
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#10B981' }}
              >
                {copied ? <><CheckCircle2 size={13} /> Copied!</> : <><Share2 size={13} /> Share Program Link</>}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Inline icon to avoid import issue
function Settings2Icon({ size, style }: { size: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M20 7H4" /><path d="M20 12H4" /><path d="M20 17H4" />
    </svg>
  );
}